/**
 * スクロール動画 (内部名 swipe_clip) の API (docs/features/swipe-motion-clip-plan.md §7.1 / §8 / §11.4)。
 * scene_id があればシーンへ差し込む受付 (/jobs/scene/swipe-clip)、無ければ素材パネルからのスタンドアロン受付 (/jobs/swipe-clip)。
 */
import { apiClient } from '../../services/apiClient';
import type { CreateJobResponse } from '../../services/asyncJobApi';
import type { SwipeClipParams } from './schema';

export interface SwipeClipJobResponse extends CreateJobResponse {
  duration_in_frames: number;
}

/** ジョブの result_data (executor の finish)。 */
export interface SwipeClipJobResult {
  material_id: string | null;
  crop_material_id: string | null;
  /** スタンドアロン (素材パネルから) は null。 */
  scene_id: string | null;
  /** シーンに差し込めたか。false なら素材ライブラリへの保存だけ (理由は reason。スタンドアロンは常に 'standalone')。 */
  attached: boolean;
  reason?: string;
}

export async function createSwipeClipJob(body: { scene_id?: string; params: SwipeClipParams }): Promise<SwipeClipJobResponse> {
  const response = body.scene_id
    ? await apiClient.post<SwipeClipJobResponse>('/jobs/scene/swipe-clip', { scene_id: body.scene_id, params: body.params })
    : await apiClient.post<SwipeClipJobResponse>('/jobs/swipe-clip', { params: body.params });
  return response.data;
}

/** attached=false の理由 → ユーザー向け文言。 */
export function swipeClipNotAttachedMessage(reason: string | undefined): string {
  switch (reason) {
    case 'aspect_ratio_changed':
      return '素材には保存しましたが、シナリオの比率が変わっていたためシーンには差し込みませんでした';
    case 'job_cancelled':
      return '素材には保存しましたが、ジョブが取り消されていたためシーンには差し込みませんでした';
    default:
      return '素材には保存しましたが、シーンが変更されていたため差し込みませんでした';
  }
}

// ============================================================
// 背景画像の AI 生成 (docs/features/swipe-motion-clip-plan.md §11.6)
// ============================================================

export interface SwipeBackgroundGuideParams {
  aspect: string;
  frame: 'phone' | 'pc';
  position: 'start' | 'center' | 'end';
  short?: number;
}

/** 構図ガイド (BE がその場で描く PNG) を Blob で取得する。表示側で URL.createObjectURL する。 */
export async function fetchCompositionGuideBlob(params: SwipeBackgroundGuideParams): Promise<Blob> {
  const response = await apiClient.get<Blob>('/jobs/swipe-clip/composition-guide', {
    params: { aspect: params.aspect, frame: params.frame, position: params.position, short: params.short ?? 540 },
    responseType: 'blob',
  });
  return response.data;
}

export interface SwipeBackgroundEstimate {
  model: string;
  credits: number;
  unit_description?: string | null;
  rate_name?: string | null;
}

export async function estimateSwipeBackground(model?: string | null, imageSize?: string | null): Promise<SwipeBackgroundEstimate> {
  const response = await apiClient.get<SwipeBackgroundEstimate>('/jobs/swipe-clip/background-estimate', {
    params: { ...(model ? { model } : {}), ...(imageSize ? { image_size: imageSize } : {}) },
  });
  return response.data;
}

export interface SwipeBackgroundGenerateBody {
  aspectRatio: string;
  frame: 'phone' | 'pc';
  framePosition: 'start' | 'center' | 'end';
  displayText: string;
  extraPrompt?: string;
  model?: string;
  image_size?: string;
  brand_id?: string | null;
  asin?: string | null;
  include_logo?: boolean;
  /** 入れるロゴ (brand_logos.id)。未指定でロゴシートを添付 */
  logo_ids?: string[];
}

export async function createSwipeBackgroundJob(body: SwipeBackgroundGenerateBody): Promise<{ job_id: string; status: string }> {
  const response = await apiClient.post<{ job_id: string; status: string }>('/jobs/swipe-clip/background-generate', body);
  return response.data;
}

/** 素材の generation_metadata.swipe_background (背景生成で残した枠 / 位置 / 比率)。 */
export interface SwipeBackgroundMeta {
  frame?: string;
  framePosition?: string;
  aspectRatio?: string;
}

export function readSwipeBackgroundMeta(metadata: Record<string, unknown> | null | undefined): SwipeBackgroundMeta | null {
  const raw = metadata?.swipe_background;
  return raw && typeof raw === 'object' ? (raw as SwipeBackgroundMeta) : null;
}

/**
 * 「素材から選ぶ」の優先順位 (§11.6): 枠 + 位置 + 比率が一致 = 3、枠 + 比率 = 2、比率だけ (メタ無しは縦横比で判定) = 1、それ以外 0。
 */
export function scoreBackgroundMaterial(
  material: { width?: number | null; height?: number | null; generation_metadata?: Record<string, unknown> | null },
  current: { frame: string; framePosition: string; aspectRatio: string },
): number {
  const meta = readSwipeBackgroundMeta(material.generation_metadata);
  if (meta) {
    const sameAspect = meta.aspectRatio === current.aspectRatio;
    const sameFrame = meta.frame === current.frame;
    const samePosition = (meta.framePosition ?? 'center') === current.framePosition;
    if (sameAspect && sameFrame && samePosition) return 3;
    if (sameAspect && sameFrame) return 2;
    if (sameAspect) return 1;
    return 0;
  }
  const w = material.width ?? 0;
  const h = material.height ?? 0;
  const [aw, ah] = current.aspectRatio.split(':').map(Number);
  if (w > 0 && h > 0 && aw > 0 && ah > 0 && Math.abs(w / h - aw / ah) < 0.02) return 1;
  return 0;
}
